import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  buildWizardStepDependencyMeta,
  ContractMasterGroupCodes,
  DEFAULT_TOASTER_LIFE,
  getWizardStepData,
  ISummaryPillItem,
  IWizardContext,
  persistWizardStepData,
  SelectOption,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { tap } from 'rxjs';
import { SelectValue, toSingleSelectValue } from '../contract-assignment-by-stand.shared';
import { ContractAssociation } from '../services/contract-association';
import type {
  ContractQueryInputDto,
  GetFomentContractsInputDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/integrations/bio-forest-abp/models';
import { IContractSelectStepData } from '../interfaces/IContractSelectStepData';
import { IContractAssociationSelectOption } from '../interfaces/IContractSelectOption';

@Component({
  selector: 'lib-step-select-contract',
  standalone: true,
  imports: [LocalizationPipe, ZnSingleSelect],
  templateUrl: './step-select-contract.html',
})
export class StepSelectContract implements WizardStepComponent<IContractSelectStepData> {
  private readonly toaster = inject(ToasterService);
  private readonly contractAssociationService = inject(ContractAssociation);
  readonly contractorSelect = viewChild('contractorSelect', { read: ZnSingleSelect });
  readonly landContractSelect = viewChild('landContractSelect', { read: ZnSingleSelect });
  readonly forestContractSelect = viewChild('forestContractSelect', { read: ZnSingleSelect });

  readonly localizationKeyPrefix = 'Geographic::ContractAssignmentByStand.StepSelectContract';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;

  landContractTypeDescription = signal('--------');
  forestContractTypeDescription = signal('--------');

  readonly selectedContractor = signal<string | null>(null);
  readonly selectedLandContract = signal<string | null>(null);
  readonly selectedForestContract = signal<string | null>(null);
  readonly contractorOptions = signal<SelectOption<string>[]>([]);
  readonly landContractOptions = signal<IContractAssociationSelectOption[]>([]);
  readonly forestContractOptions = signal<IContractAssociationSelectOption[]>([]);

  private readonly contractors = new Map<string, string>();
  private readonly landContracts = new Map<string, IContractAssociationSelectOption>();
  private readonly forestContracts = new Map<string, IContractAssociationSelectOption>();

  private stepData: IContractSelectStepData = {} as IContractSelectStepData;

  loadVendors = (params?: ZnSelectQueryParams) => this.contractAssociationService.loadVendors({
    ...params,
  }).pipe(
    tap(response => {
      response.items.forEach(item => {
        this.contractors.set(item.key, item.value);
      });
    }),
  );

  loadLandContracts = (params?: ContractQueryInputDto) => this.contractAssociationService.loadContracts({
    ...params,
    contractMasterGroupCode: ContractMasterGroupCodes.Land,
    vendorId: Number(this.selectedContractor()),
    isActive: true
  }).pipe(
    tap(response => {
      response.items.forEach(item => {
        this.landContracts.set(item.key, item);
      });
    }),
  );
  loadForestContracts = (params?: GetFomentContractsInputDto & Omit<ZnSelectQueryParams, 'ids'>) => this.contractAssociationService.loadFomentContracts({
    ...params,
    vendorId: Number(this.selectedContractor())
  }).pipe(
    tap(response => {
      response.items.forEach(item => {
        this.forestContracts.set(item.key, item);
      });
    }),
  );

  readonly contractSectionClass = computed(() =>
    this.selectedContractor()
      ? 'zn-step-content-section'
      : 'zn-step-content-section zn-step-content-section--selected',
  );

  readonly contractAssociationSectionClass = computed(() => {
    if (!this.selectedContractor()) {
      return 'zn-full-height zn-step-content-section zn-step-content-section--disabled';
    }

    return 'zn-full-height zn-step-content-section zn-step-content-section--selected';
  });

  onContractorChange(value: SelectValue): void {
    const contractorId = toSingleSelectValue(value);
    const contractorName = contractorId ? this.contractors.get(contractorId) : null;

    this.resetContractSelections();
    this.selectedContractor.set(contractorId);
    this.contractorOptions.set(
      contractorId && contractorName ? [{ key: contractorId, value: contractorName }] : [],
    );

    this.stepData = {
      ...this.stepData,
      contractorId: this.selectedContractor(),
      contractorName: contractorName ?? null,
      landContractToAssociate: null,
      forestContractToAssociate: null,
    };

    this.selectedLandContract.set(null);
    this.selectedForestContract.set(null);
    this.landContractOptions.set([]);
    this.forestContractOptions.set([]);
    this.landContractTypeDescription.set('--------');
    this.forestContractTypeDescription.set('--------');
    this.landContracts.clear();
    this.forestContracts.clear();
  }

  onLandContractChange(value: SelectValue): void {
    const landContractId = toSingleSelectValue(value);
    const landContract =
      landContractId ?
        (this.landContracts.get(landContractId) ?? this.stepData.landContractToAssociate)
      : null;

    this.stepData = {
      ...this.stepData,
      landContractToAssociate: landContract,
    };
    this.selectedLandContract.set(landContractId);
    this.landContractOptions.set(landContract ? [landContract] : []);
    this.landContractTypeDescription.set(landContract?.description ?? '--------');
  }

  onForestContractChange(value: SelectValue): void {
    const forestContractId = toSingleSelectValue(value);
    const forestContract =
      forestContractId ?
        (this.forestContracts.get(forestContractId) ?? this.stepData.forestContractToAssociate)
      : null;

    this.stepData = {
      ...this.stepData,
      forestContractToAssociate: forestContract,
    };
    this.selectedForestContract.set(forestContractId);
    this.forestContractOptions.set(forestContract ? [forestContract] : []);
    this.forestContractTypeDescription.set(forestContract?.description ?? '--------');
  }

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const previousData: IContractSelectStepData | undefined = getWizardStepData(context, context.activeStepId());

    if(previousData) this.loadPreviousData(previousData, context);
  }

  canLeave(): boolean {
    if (this.hasRequiredSelection()) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::ContractAssignmentByStand.StepSelectContract.cantContinue`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  canLeaveSync(): boolean {
    return true;
  }

  hasMeaningfulData(): boolean {
    return (
      !!this.selectedContractor() ||
      !!this.selectedLandContract() ||
      !!this.selectedForestContract() ||
      this.hasTypedSearchValue()
    );
  }

  collect(context: IWizardContext): IContractSelectStepData {
    persistWizardStepData(context, {
      valid: true,
      visited: true,
      completed: true,
      data: this.stepData,
      dirty: true,
      meta: buildWizardStepDependencyMeta(this.stepData),
      invalidateStepIds: [2, 3, 4],
    });
    return this.stepData;

  }

  buildSummary(data: IContractSelectStepData): ISummaryPillItem[] {
    return [
      data.contractorName
        ? {
            label: `${this.localizationKeyPrefix}.Contract.Contractor`,
            value: data.contractorName,
          }
        : null,
      data.landContractToAssociate?.value
        ? {
            label: `${this.localizationKeyPrefix}.ContractAssociation.LandContractToAssociate`,
            value: data.landContractToAssociate.value,
          }
        : null,
      data.forestContractToAssociate?.value
        ? {
            label: `${this.localizationKeyPrefix}.ContractAssociation.ForestContractToAssociate`,
            value: data.forestContractToAssociate.value,
          }
        : null,
    ].filter(Boolean) as ISummaryPillItem[];
  }

  private hasRequiredSelection(): boolean {
    return !!(
      this.selectedContractor() &&
      (this.selectedLandContract() ||
      this.selectedForestContract())
    );
  }

  private resetContractSelections(): void {
    this.landContractSelect()?.writeValue(null);
    this.forestContractSelect()?.writeValue(null);
  }

  private hasTypedSearchValue(): boolean {
    return (
      this.hasSearchValue(this.contractorSelect()) ||
      this.hasSearchValue(this.landContractSelect()) ||
      this.hasSearchValue(this.forestContractSelect())
    );
  }

  private hasSearchValue(select: ZnSingleSelect | undefined): boolean {
    return !!select?.search().trim();
  }

  private loadPreviousData(data: IContractSelectStepData, context: IWizardContext): void {
    this.stepData = data;
    this.selectedContractor.set(data.contractorId);
    this.selectedForestContract.set(data.forestContractToAssociate?.key ?? null);
    this.selectedLandContract.set(data.landContractToAssociate?.key ?? null);
    this.contractorOptions.set(
      data.contractorId && data.contractorName
        ? [{ key: data.contractorId, value: data.contractorName }]
        : [],
    );
    this.landContractOptions.set(
      data.landContractToAssociate ? [data.landContractToAssociate] : [],
    );
    this.forestContractOptions.set(
      data.forestContractToAssociate ? [data.forestContractToAssociate] : [],
    );
    this.forestContractTypeDescription.set(data.forestContractToAssociate?.description ?? '--------');
    this.landContractTypeDescription.set(data.landContractToAssociate?.description ?? '--------');

    queueMicrotask(() => {
      this.contractorSelect()?.writeValue(data.contractorId);
      this.landContractSelect()?.writeValue(data.landContractToAssociate?.key ?? null);
      this.forestContractSelect()?.writeValue(data.forestContractToAssociate?.key ?? null);
    });

    context.patchStepState(context.activeStepId(), {
      visited: true,
      dirty: true,
      completed: true,
      data: data
    });
  }
}
